import React from 'react';
import { Box } from '@mui/material';
import { LegendType, PieVariant } from '../../../charts/core/Chart.types';
import { LegendSlotProps } from '../../../charts/core/legend/Legend';
import { TableLegendCells } from '../../../charts/core/legend/table';
import { PieSeries } from '../../../charts/radial/pie/PieSeries';
import { NO_VALUE } from '../../../views/CustomDashboards/constants';
import { PieWidgetTooltip } from './PieWidgetTooltip';

export type PieWidgetDatum = Record<string, unknown>;

type LegendProps = {
  showValue?: boolean;
  showShare?: boolean;
};

type PieWidgetCloneAdapterProps = {
  data?: PieWidgetDatum[];
  metricKey?: string;
  valKey?: string;
  variant?: PieVariant;
  legendType?: LegendType;
  palette?: string[];
  numOfItems?: number;
  metricNameFormatter?: (value: unknown) => React.ReactNode;
  legendProps?: LegendProps;
};

const getTopItemsPlusOthers = (data: PieWidgetDatum[], metricKey: string, valKey: string, numOfItems: number): PieWidgetDatum[] => {
  if (data.length <= numOfItems + 1) return data;
  const top = data.slice(0, numOfItems);
  const rest = data.slice(numOfItems);
  const othersValue = rest.reduce((sum, datum) => sum + (Number(datum[valKey]) || 0), 0);
  return [...top, { ...rest[0], [metricKey]: 'Others', [valKey]: othersValue }];
};

const buildLegendSlotProps = ({ showValue = true, showShare = false }: LegendProps): LegendSlotProps<PieWidgetDatum> | undefined => {
  if (showValue && showShare) return undefined;
  const columnCount = [true, showValue, showShare].filter(Boolean).length;
  const columns = columnCount === 1 ? '1fr' : `1fr ${'max-content '.repeat(columnCount - 1).trim()}`;
  return {
    tableLegend: {
      root: { sx: { gridTemplateColumns: columns } },
      row: { children: <TableLegendCells showValue={showValue} showShare={showShare} /> },
    },
  };
};

export const PieWidgetCloneAdapter = ({
  data = [],
  metricKey = 'category',
  valKey = 'count',
  variant = 'pie',
  legendType = LegendType.tableRight,
  palette,
  numOfItems,
  metricNameFormatter,
  legendProps = { showValue: true, showShare: false },
}: PieWidgetCloneAdapterProps) => {
  const chartData = numOfItems ? getTopItemsPlusOthers(data, metricKey, valKey, numOfItems) : data;
  const total = chartData.reduce((sum, datum) => sum + (Number(datum[valKey]) || 0), 0);
  const slotProps = buildLegendSlotProps(legendProps);

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: '100%', width: '100%', overflow: 'hidden' }}>
      <PieSeries
        data={chartData}
        idAccessor={datum => String(datum[metricKey])}
        valueAccessor={datum => Number(datum[valKey]) || 0}
        labelFormatter={datum => {
          if (metricNameFormatter) return metricNameFormatter(datum[metricKey]);
          if (datum[metricKey] === null) return NO_VALUE;
          return String(datum[metricKey]);
        }}
        valueFormatter={datum => (Number(datum[valKey]) || 0).toLocaleString()}
        variant={variant}
        legendType={legendType}
        slotProps={slotProps}
        TooltipComponent={PieWidgetTooltip}
        tooltipComponentProps={{ total }}
        {...(palette ? { palette } : {})}
      />
    </Box>
  );
};
